import {useService} from '@cindedi/react-application';
import {abort} from '@cindedi/request-utilities/abort';
import {A} from '@cindedi/ui/components/A';
import {Card} from '@cindedi/ui/components/Card';
import {H2} from '@cindedi/ui/components/Heading/H2';
import {Slot} from '@cindedi/ui/components/Slot';
import {isRecord} from '@cindedi/utilities/isRecord';
import {truncate} from '@cindedi/utilities/truncate';
import type {MetaFunction, LoaderFunction} from '@remix-run/node';
import {json} from '@remix-run/node';
import {useLoaderData} from '@remix-run/react';
import {formatEventDate} from '~/domains/event/utilities/formatEventDate';
import {formatPrice} from '~/domains/event/utilities/formatPrice';
import {formatSize} from '~/domains/file-system/utilities/formatSize';
import {PublicLayout} from '~/domains/organization/components/PublicLayout';

export interface Encontros$SlugLoaderData {
  convention: Event.PublicConventionDto;
}

export const meta: MetaFunction = function Encontros$SlugMeta({data}) {
  return isRecord(data) && data.convention
    ? [
        {title: `${data.convention.name} | Cindedi`},
        {
          name: 'description',
          content: truncate(data.convention.textDescription, 155),
        },
      ]
    : [{title: 'Encontro - Cindedi'}];
};

export const loader: LoaderFunction = async function Encontros$SlugLoader({
  context: {use},
  params,
}) {
  const Convention = use('Convention');
  const {slug} = params;

  if (!slug) return abort(404);

  const convention = await Convention.find({where: {slug}});

  if (convention == null) return abort(404);

  return json({convention});
};

export default function Encontros$SlugView() {
  const {convention} = useLoaderData<Encontros$SlugLoaderData>();
  useService('organization/titlebar').setTitle(convention.name);

  return (
    <PublicLayout cover={convention.cover?.url}>
      <div className="flex gap-4 max-w-full flex-col lg:flex-row">
        <div className="flex flex-col flex-1 gap-2 pt-4 max-w-full">
          <div className="flex gap-2 text-primary-8 text-sm items-center mt-1 bg-primary-1 p-4 rounded mb-4">
            <span>
              De {formatEventDate(convention.startsAt)} à {formatEventDate(convention.endsAt)}
            </span>
            •<span>{formatPrice(convention.price)}</span>
          </div>
          <div dangerouslySetInnerHTML={{__html: convention.description}} />
          {convention.support && convention.support.length > 0 && (
            <div>
              <H2 size="md" className="pt-4">
                Apoio
              </H2>
              <ul role="list" className="flex items-center gap-4">
                {convention.support.map((agency) => (
                  <li key={agency.id}>
                    <A target="_blank" href={agency.url}>
                      <img
                        src={agency.cover?.url}
                        alt={agency.name}
                        className="max-h-20 max-w-32"
                      />
                    </A>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {convention.files.length > 0 && (
          <div className="flex flex-col w-full lg:w-72 gap-4">
            <Card aria-label={`Arquivos anexados`} className="p-4">
              <Slot name="title">
                <H2 className="pl-4 pt-4">Anexos</H2>
              </Slot>
              <ul>
                {convention.files.map((file) => (
                  <li key={file.publicId} className="mb-4 break-words">
                    <A target="_blank" href={file.url}>
                      <div className="flex flex-col">
                        <span className="text-primary-12">{file.name}</span>
                        <span className="text-sm text-shade-11 flex gap-2">
                          <span>{formatSize(file.size)}</span>•<span>{file.extension}</span>
                        </span>
                      </div>
                    </A>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
